import { z } from 'zod';

const ratingSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  value: z.number(),
  color: z.string(),
});

/** Rating with V2's primitive constraints — write path only. */
const writeRatingSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  value: z.number().int().positive('Value must be a positive integer'),
  color: z.string().min(1, 'Color is required'),
});

export const riskAssessmentResultConfigSchema = z.object({
  likelihood: z.object({
    ratings: z.array(ratingSchema),
  }),
  impact: z.object({
    ratings: z.array(ratingSchema),
    categories: z.array(z.object({ name: z.string(), color: z.string() })),
    aggregation: z.enum(['average', 'maximum']).optional(),
  }),
  matrix: z.array(
    z.object({
      title: z.string(),
      value: z.number(),
      color: z.string(),
      likelihood: z.number(),
      impact: z.number(),
    })
  ),
});

export type RiskAssessmentResultConfig = z.infer<
  typeof riskAssessmentResultConfigSchema
>;

const hasUniqueValues = <T>(
  items: T[],
  accessor: (item: T) => unknown
): boolean => new Set(items.map(accessor)).size === items.length;

/**
 * COI-633 — the WRITE contract for a scoring config.
 *
 * `riskAssessmentResultConfigSchema` above is a shape check, and it must stay one:
 * three web reads `.parse()` STORED configs through it
 * (`useGetLatestRiskAssessmentResultConfig`, `useGetRiskAssessmentResultConfigByIds`,
 * `version-history.ts`), so tightening it in place would make the risk-scoring
 * pages throw for exactly the orgs whose config is already invalid. Validation
 * therefore lives in a separate schema applied on create/update only.
 *
 * These are V2's rules from `packages/rest-api/src/handlers/risk-assessment-result-config/schema.ts`,
 * which V3 dropped entirely. Why each one matters: `calculateRating` resolves a
 * rating by finding the matrix cell for a (likelihood, impact) pair and returns
 * `null` when there is none — so an incomplete matrix does not fail at save time,
 * it silently stores every subsequent risk rating in that org with no overall
 * rating.
 */
export const riskAssessmentResultConfigWriteSchema = z
  .object({
    likelihood: z.object({
      ratings: z
        .array(writeRatingSchema)
        .min(1, 'At least one likelihood rating is required'),
    }),
    impact: z
      .object({
        ratings: z
          .array(writeRatingSchema)
          .min(1, 'At least one impact rating is required'),
        categories: z.array(
          z.object({
            name: z.string().min(1, 'Category name is required'),
            color: z.string().min(1, 'Color is required'),
          })
        ),
        aggregation: z.enum(['average', 'maximum']).optional(),
      })
      .superRefine((impact, ctx) => {
        // A single category is neither single-impact (0) nor multi-impact (2+).
        if (impact.categories.length === 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Must have 0 categories (single impact) or 2+ categories (multi-impact)',
            path: ['categories'],
          });
        }
        if (impact.categories.length > 1 && !impact.aggregation) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'aggregation is required when multiple impact categories are defined',
            path: ['aggregation'],
          });
        }
      }),
    matrix: z
      .array(
        z.object({
          title: z.string().min(1, 'Title is required'),
          value: z.number().int().positive('Value must be a positive integer'),
          color: z.string().min(1, 'Color is required'),
          likelihood: z
            .number()
            .int()
            .positive('Likelihood must be a positive integer'),
          impact: z
            .number()
            .int()
            .positive('Impact must be a positive integer'),
        })
      )
      .min(1, 'At least one matrix entry is required'),
  })
  .superRefine((config, ctx) => {
    // Five uniqueness rules, spelled out rather than looped: a generic loop over
    // heterogeneous accessors needs casts, which this repo bans, and the explicit
    // form is what V2 had.
    if (!hasUniqueValues(config.likelihood.ratings, (r) => r.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Likelihood rating values must be unique',
        path: ['likelihood', 'ratings'],
      });
    }
    if (!hasUniqueValues(config.likelihood.ratings, (r) => r.title)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Likelihood rating titles must be unique',
        path: ['likelihood', 'ratings'],
      });
    }
    if (!hasUniqueValues(config.impact.ratings, (r) => r.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Impact rating values must be unique',
        path: ['impact', 'ratings'],
      });
    }
    if (!hasUniqueValues(config.impact.ratings, (r) => r.title)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Impact rating titles must be unique',
        path: ['impact', 'ratings'],
      });
    }
    if (!hasUniqueValues(config.impact.categories, (c) => c.name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Impact category names must be unique',
        path: ['impact', 'categories'],
      });
    }

    const validLikelihoodValues = new Set(
      config.likelihood.ratings.map((r) => r.value)
    );
    const validImpactValues = new Set(
      config.impact.ratings.map((r) => r.value)
    );

    // Every cell must reference a declared rating on both axes, or the cell is
    // unreachable and the pair it claims to cover is really missing.
    const pairs: string[] = [];
    for (const cell of config.matrix) {
      if (!validLikelihoodValues.has(cell.likelihood)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Likelihood value ${cell.likelihood} does not exist in likelihood ratings`,
          path: ['matrix'],
        });
      }
      if (!validImpactValues.has(cell.impact)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Impact value ${cell.impact} does not exist in impact ratings`,
          path: ['matrix'],
        });
      }
      pairs.push(`${cell.likelihood}-${cell.impact}`);
    }

    // Two cells for one pair make the resolved rating depend on array order.
    if (new Set(pairs).size !== pairs.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Duplicate likelihood/impact pairs found in matrix',
        path: ['matrix'],
      });
    }

    // Completeness: this is the rule that stops `calculateRating` returning null.
    const seen = new Set(pairs);
    const missing: string[] = [];
    for (const likelihood of validLikelihoodValues) {
      for (const impact of validImpactValues) {
        const pair = `${likelihood}-${impact}`;
        if (!seen.has(pair)) {
          missing.push(pair);
        }
      }
    }

    if (missing.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Matrix is incomplete. Missing likelihood/impact pairs: ${missing.join(', ')}`,
        path: ['matrix'],
      });
    }
  });

type DefaultRatingValue = 1 | 2 | 3 | 4 | 5;

// Rating title + colour by result value. Colours match the default taxonomy
// so ratings render identically.
const DEFAULT_RATING_BY_VALUE: Record<
  DefaultRatingValue,
  { title: string; color: string }
> = {
  1: { title: 'Minimal', color: '#6DAC3F' },
  2: { title: 'Low', color: '#8CC862' },
  3: { title: 'Moderate', color: '#F2A041' },
  4: { title: 'High', color: '#E37373' },
  5: { title: 'Critical', color: '#CE1B1B' },
};

// Result rating value per [likelihood][impact] (both 1-5).
const DEFAULT_MATRIX_VALUES: DefaultRatingValue[][] = [
  [1, 2, 2, 2, 2],
  [2, 2, 2, 3, 3],
  [2, 2, 3, 3, 4],
  [2, 3, 3, 4, 4],
  [2, 3, 4, 4, 5],
];

/**
 * Default risk scoring config, used when the scoring-settings flag is on but an
 * org has no stored config. Reproduces the default taxonomy ratings exactly, so
 * resolving against it changes nothing visible. Deliberately taxonomy-free so it
 * survives removal of the i18n taxonomy — do not derive it from that.
 */
export const DEFAULT_RISK_ASSESSMENT_RESULT_CONFIG: RiskAssessmentResultConfig =
  {
    likelihood: {
      ratings: [
        { title: 'Very Unlikely', value: 1, color: '#6DAC3F' },
        { title: 'Unlikely', value: 2, color: '#8CC862' },
        { title: 'Likely', value: 3, color: '#F2A041' },
        { title: 'Very Likely', value: 4, color: '#E37373' },
        { title: 'Certain', value: 5, color: '#CE1B1B' },
      ],
    },
    impact: {
      ratings: [
        { title: 'Minimal', value: 1, color: '#6DAC3F' },
        { title: 'Low', value: 2, color: '#8CC862' },
        { title: 'Moderate', value: 3, color: '#F2A041' },
        { title: 'High', value: 4, color: '#E37373' },
        { title: 'Critical', value: 5, color: '#CE1B1B' },
      ],
      categories: [],
    },
    matrix: DEFAULT_MATRIX_VALUES.flatMap((likelihoodRow, likelihoodIndex) =>
      likelihoodRow.map((value, impactIndex) => ({
        ...DEFAULT_RATING_BY_VALUE[value],
        value,
        likelihood: likelihoodIndex + 1,
        impact: impactIndex + 1,
      }))
    ),
  };
